import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle, AlertTriangle, BarChart3, Network, Workflow, X } from 'lucide-react';

export const PipelineResultsModal = ({ isOpen, onClose, results }) => {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !results) return null;

  const { num_nodes, num_edges, is_dag } = results;

  const statusIcon = is_dag ? (
    <CheckCircle className="w-6 h-6 text-green-500" />
  ) : (
    <AlertTriangle className="w-6 h-6 text-red-500" />
  );

  const statusColor = is_dag ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200";
  const badgeVariant = is_dag ? "default" : "destructive";

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'fixed'
      }}
    >
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />

      <Card className="relative z-10 w-full max-w-md bg-white shadow-2xl border-0 rounded-lg">
        <div className="p-6">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              {statusIcon}
              <h2 className="text-xl font-semibold">Pipeline Analysis Complete</h2>
            </div>
            <p className="text-sm text-gray-600">
              Your pipeline has been analyzed for structure and validity.
            </p>
          </div>

          <div className="space-y-4">
            <Card className={`p-4 ${statusColor} border`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">DAG Status</span>
                <Badge variant={badgeVariant} className="flex items-center gap-1">
                  <Workflow className="w-3 h-3" />
                  {is_dag ? "Valid DAG" : "Contains Cycles"}
                </Badge>
              </div>
              <p className="text-sm text-gray-600">
                {is_dag
                  ? "Your pipeline is valid and can be executed safely."
                  : "Warning: Your pipeline contains cycles which may cause infinite loops."}
              </p>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4 border">
                <div className="flex items-center gap-2 mb-1">
                  <Network className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium">Nodes</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">{num_nodes}</div>
              </Card>

              <Card className="p-4 border">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-medium">Edges</span>
                </div>
                <div className="text-2xl font-bold text-purple-600">{num_edges}</div>
              </Card>
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={onClose} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
              Continue Editing
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  return createPortal(modalContent, document.body);
};